import {
  Tag,
  TagPicker,
  TagPickerControl,
  TagPickerGroup,
  TagPickerList,
  TagPickerOption,
  TagPickerProps,
  Tooltip,
} from "@fluentui/react-components";
import { useEffect, useState } from "react";
import { CustomOption } from "../peoplePicker/PeoplePicker.types";
import { useStyles } from "./TagPicker.styles";
import { ITagPickerCustomProps } from "./TagPicker.types";

export const TagPickerCustom = (props: ITagPickerCustomProps): JSX.Element => {
  const { stackSelectedVertical = false, singleSelect = false } = props;
  const styles = useStyles();
  const [selectedOptions, setSelectedOptions] = useState<CustomOption[]>(
    props.selectedItems,
  );
  const [availableOptions, setAvailableOptions] = useState<CustomOption[]>([]);

  useEffect((): void => {
    const newAvailableOptions: CustomOption[] = props.items.filter(
      (item: CustomOption): boolean =>
        !selectedOptions.some(
          (selectedItem: CustomOption): boolean =>
            selectedItem.key === item.key,
        ),
    );

    setAvailableOptions(newAvailableOptions);
  }, [selectedOptions, props.items]);

  useEffect((): void => {
    setSelectedOptions(props.selectedItems);
  }, [props.selectedItems]);

  const onOptionSelect: TagPickerProps["onOptionSelect"] = (event, data) => {
    if (singleSelect) {
      const lastOption = data.selectedOptions[data.selectedOptions.length - 1];
      if (lastOption) {
        const matched = props.items.find((item) => item.text === lastOption);
        const newSelected = matched ? [matched] : [];
        setSelectedOptions(newSelected);
        props.onSelectionChange(newSelected);
      } else {
        setSelectedOptions([]);
        props.onSelectionChange([]);
      }
      return;
    }

    const newSelected: CustomOption[] = data.selectedOptions.map(
      (option) => props.items.find((item) => item.text === option)!,
    );

    setSelectedOptions(newSelected);
    props.onSelectionChange(newSelected);
  };

  const handleSelectItem = (item: CustomOption): void => {
    if (singleSelect) {
      setSelectedOptions([item]);
      props.onSelectionChange([item]);
      return;
    }

    const isSelected = selectedOptions.some((s) => s.key === item.key);
    const newSelectedItems = isSelected
      ? selectedOptions.filter((s) => s.key !== item.key)
      : [...selectedOptions, item];

    setSelectedOptions(newSelectedItems);
    props.onSelectionChange(newSelectedItems);
  };

  return (
    <TagPicker
      selectedOptions={selectedOptions.map((s: CustomOption): string => s.text)}
      onOptionSelect={onOptionSelect}
      positioning={{ position: "below", pinned: true }}
    >
      <TagPickerControl>
        <TagPickerGroup
          aria-label="Selected Items"
          className={
            stackSelectedVertical ? styles.groupColumn : styles.groupRow
          }
        >
          {selectedOptions.map((item) => (
            <Tooltip content={item.text} relationship="label" key={item.key}>
              <Tag
                className={styles.tag}
                appearance="brand"
                shape="rounded"
                value={item.text}
                disabled={props.disabledOptions?.includes(item.key) || false}
              >
                <span className={styles.tagText}>{item.text}</span>
              </Tag>
            </Tooltip>
          ))}
        </TagPickerGroup>
      </TagPickerControl>
      <TagPickerList className={styles.tagPickerList}>
        {availableOptions.length > 0
          ? availableOptions.map(
              (item: CustomOption): JSX.Element => (
                <TagPickerOption
                  key={item.key}
                  value={item.text}
                  onClick={() => handleSelectItem(item)}
                >
                  {item.text}
                </TagPickerOption>
              ),
            )
          : "No options available"}
      </TagPickerList>
    </TagPicker>
  );
};
